import { useRef, useState, useCallback } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Avatar, AvatarFallback, AvatarImage, Button, cn } from '@pantry/ui';
import { useUploadAvatar, useDeleteAvatar } from '@pantry/api-client';
import { toast } from 'sonner';
import { CameraIcon, TrashIcon } from '@heroicons/react/20/solid';

interface AvatarUploadProps {
  currentAvatarUrl: string | null | undefined;
  userName: string | null | undefined;
  userEmail: string | null | undefined;
  onAvatarChange?: (url: string | null) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

function getInitials(name: string | null | undefined, email: string | null | undefined): string {
  if (name) {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return 'U';
}

const sizeClasses = {
  sm: 'h-12 w-12',
  md: 'h-16 w-16',
  lg: 'h-24 w-24',
};

const iconSizeClasses = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

export function AvatarUpload({
  currentAvatarUrl,
  userName,
  userEmail,
  onAvatarChange,
  size = 'md',
  className,
}: AvatarUploadProps) {
  const { t } = useLingui();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const uploadAvatar = useUploadAvatar();
  const deleteAvatar = useDeleteAvatar();

  const isLoading = uploadAvatar.isPending || deleteAvatar.isPending;

  const handleFileSelect = useCallback(
    async (file: File) => {
      // Validate file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(t`Invalid file type. Please upload a JPEG, PNG, WebP, or GIF image.`);
        return;
      }

      // Validate file size
      if (file.size > MAX_SIZE_BYTES) {
        toast.error(t`File too large. Maximum size is 2MB.`);
        return;
      }

      // Create preview
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);

      try {
        const result = await uploadAvatar.mutateAsync(file);
        onAvatarChange?.(result.avatarUrl);
        toast.success(t`Avatar uploaded successfully`);
      } catch (error) {
        setPreviewUrl(null);
        const message = error instanceof Error ? error.message : t`Failed to upload avatar`;
        toast.error(message);
      } finally {
        // Cleanup preview URL
        URL.revokeObjectURL(objectUrl);
        setPreviewUrl(null);
      }
    },
    [uploadAvatar, onAvatarChange, t]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDeleteAvatar = async () => {
    try {
      await deleteAvatar.mutateAsync();
      onAvatarChange?.(null);
      toast.success(t`Avatar removed`);
    } catch (error) {
      const message = error instanceof Error ? error.message : t`Failed to remove avatar`;
      toast.error(message);
    }
  };

  const handleClick = () => {
    if (!isLoading) {
      fileInputRef.current?.click();
    }
  };

  const displayUrl = previewUrl || currentAvatarUrl || undefined;
  const initials = getInitials(userName, userEmail);

  return (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        onChange={handleInputChange}
        className="hidden"
        disabled={isLoading}
      />

      {/* Avatar with overlay */}
      <div
        className={cn('relative cursor-pointer group', isLoading && 'cursor-wait opacity-70')}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        aria-label={t`Upload avatar`}
      >
        <Avatar
          className={cn(
            sizeClasses[size],
            'ring-2 ring-transparent transition-all',
            isDragOver && 'ring-primary',
            'group-hover:ring-primary/50'
          )}
        >
          <AvatarImage src={displayUrl} alt={userName || 'User avatar'} />
          <AvatarFallback className="text-lg font-medium">{initials}</AvatarFallback>
        </Avatar>

        {/* Upload overlay */}
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center rounded-full',
            'bg-black/50 opacity-0 transition-opacity',
            'group-hover:opacity-100',
            isDragOver && 'opacity-100'
          )}
        >
          {isLoading ? (
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <CameraIcon className={cn(iconSizeClasses[size], 'text-white')} />
          )}
        </div>
      </div>

      {/* Helper text */}
      <p className="text-xs text-muted-foreground text-center">
        <Trans>Click or drag to upload</Trans>
        <br />
        <span className="text-[10px]">
          <Trans>JPEG, PNG, WebP, GIF (max 2MB)</Trans>
        </span>
      </p>

      {/* Delete button (only shown if avatar exists) */}
      {currentAvatarUrl && !isLoading && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteAvatar();
          }}
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          <TrashIcon className="h-4 w-4 mr-1" />
          <Trans>Remove</Trans>
        </Button>
      )}
    </div>
  );
}
